import React, { useMemo, useCallback } from "react";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Search, Filter, X } from "lucide-react";
import { Button } from "../ui/button";

// Filter configuration interface
interface FilterConfig {
  key: string;
  label: string;
  options: string[];
  placeholder: string;
}

interface GenericLedgerControlBarProps {
  filters: Record<string, string>;
  onFiltersChange: (filters: Record<string, string>) => void;
  filterConfigs: FilterConfig[];
  loading?: boolean;
  totalResults?: number;
  searchPlaceholder?: string;
  searchKey?: string;
}

const GenericLedgerControlBar: React.FC<GenericLedgerControlBarProps> =
  React.memo(
    ({
      filters,
      onFiltersChange,
      filterConfigs,
      loading = false,
      totalResults,
      searchPlaceholder = "Search...",
      searchKey = "search",
    }) => {
      const handleSearchChange = useCallback(
        (value: string) => {
          onFiltersChange({
            ...filters,
            [searchKey]: value,
          });
        },
        [filters, onFiltersChange, searchKey],
      );

      const handleFilterChange = useCallback(
        (key: string, value: string) => {
          onFiltersChange({
            ...filters,
            [key]: value === "all" ? "" : value,
          });
        },
        [filters, onFiltersChange],
      );

      const clearFilters = useCallback(() => {
        const clearedFilters: Record<string, string> = {};
        Object.keys(filters).forEach((key) => {
          clearedFilters[key] = "";
        });
        onFiltersChange(clearedFilters);
      }, [filters, onFiltersChange]);

      const hasActiveFilters = useMemo(
        () =>
          Object.values(filters).some((value) => value && value.trim() !== ""),
        [filters],
      );

      // Memoize filter options to prevent re-renders
      const memoizedFilterConfigs = useMemo(
        () =>
          filterConfigs.map((config) => ({
            ...config,
            options: config.options.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            )),
          })),
        [filterConfigs],
      );

      const getFilterDisplayValue = (key: string, value: string): string => {
        if (!value) return "";
        const config = filterConfigs.find((c) => c.key === key);
        return config ? value : value;
      };

      return (
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex flex-col space-y-3">
              {/* Search and Results Info */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={searchPlaceholder}
                    value={filters[searchKey] || ""}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {totalResults !== undefined && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {loading ? (
                      <span>Loading...</span>
                    ) : (
                      <span>
                        {totalResults.toLocaleString()} result
                        {totalResults !== 1 ? "s" : ""}
                        {hasActiveFilters && " (filtered)"}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Filters */}
              {filterConfigs.length > 0 && (
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex flex-col sm:flex-row gap-4 flex-1">
                    {memoizedFilterConfigs.map((config) => (
                      <div key={config.key} className="flex-1 min-w-0">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <Filter className="inline h-4 w-4 mr-1" />
                          {config.label}
                        </label>
                        <Select
                          value={filters[config.key] || "all"}
                          onValueChange={(value) =>
                            handleFilterChange(config.key, value)
                          }
                          disabled={loading}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={config.placeholder} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">
                              {config.placeholder}
                            </SelectItem>
                            {config.options}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>

                  {/* Clear Filters Button */}
                  {hasActiveFilters && (
                    <div className="flex items-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={clearFilters}
                        disabled={loading}
                        className="whitespace-nowrap"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Clear Filters
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Active Filters Display */}
              {hasActiveFilters && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Active filters:
                  </span>

                  {Object.entries(filters).map(([key, value]) => {
                    if (!value || value.trim() === "") return null;

                    const config = filterConfigs.find((c) => c.key === key);
                    const isSearch = key === searchKey;

                    return (
                      <span
                        key={key}
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                          isSearch
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
                            : key === "cohort"
                              ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300"
                              : "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300"
                        }`}
                      >
                        {isSearch
                          ? `Search: "${value}"`
                          : `${config?.label || key}: ${getFilterDisplayValue(key, value)}`}
                        <button
                          onClick={() => handleFilterChange(key, "all")}
                          className={`ml-1 ${
                            isSearch
                              ? "hover:text-blue-600 dark:hover:text-blue-200"
                              : key === "cohort"
                                ? "hover:text-green-600 dark:hover:text-green-200"
                                : "hover:text-purple-600 dark:hover:text-purple-200"
                          }`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      );
    },
  );

GenericLedgerControlBar.displayName = "GenericLedgerControlBar";

export default GenericLedgerControlBar;

// Predefined filter configurations for each ledger type

export const TARGET_LEDGER_FILTER_CONFIGS: FilterConfig[] = [
  {
    key: "cohort",
    label: "Cohort",
    options: [], // Will be populated from service
    placeholder: "All Cohorts",
  },
  {
    key: "addedBy",
    label: "Added By",
    options: [], // Will be populated from service
    placeholder: "All Users",
  },
];

export const COHORT_LEDGER_FILTER_CONFIGS: FilterConfig[] = [
  {
    key: "cohort",
    label: "Cohort",
    options: [], // Will be populated from service
    placeholder: "All Cohorts",
  },
  {
    key: "yourName",
    label: "Your Name",
    options: [], // Will be populated from service
    placeholder: "All Names",
  },
];

export const MAILING_LEDGER_FILTER_CONFIGS: FilterConfig[] = [
  {
    key: "cohort",
    label: "Cohort",
    options: [], // Will be populated from service
    placeholder: "All Cohorts",
  },
  {
    key: "addressingType",
    label: "Addressing Type",
    options: [], // Will be populated from service
    placeholder: "All Types",
  },
  {
    key: "includeInMailingCostDashboard",
    label: "Include in Dashboard",
    options: ["Yes", "No"], // Static options
    placeholder: "All Status",
  },
  {
    key: "addedBy",
    label: "Entered by",
    options: [], // Will be populated from service
    placeholder: "All Users",
  },
];
